import type {
  AddEventTranslationReq,
  BaseErrorResponse,
  EmptySuccessResponse,
  GetEventTranslationListRes,
  GetEventTranslationRes,
  GetEventTypeListRes,
  GetEventTypeRes,
  UpdateEventTranslationReq,
  UpdateEventTypeReq,
  GetEventTemplatesRes,
  GetEventTemplateRes,
  AddEventTemplateReq,
  UpdateEventTemplateReq,
  GetEventChannelsRes,
} from './data-contracts';
import type { RequestParams } from './http-client';
import { ContentType, HttpClient } from './http-client';


export default function useEvent() {
  class Event<SecurityDataType = unknown> extends HttpClient<SecurityDataType> {
    /**
     * No description
     *
     * @tags Event channel
     * @name EventChannelControllerGetEventChannels
     * @summary Returns a list of event channels
     * @request GET:/api3/event/channel
     * @secure
     * @response `200` `GetEventChannelsRes`
     * @response `400` `BaseErrorResponse`
     */
    eventChannelControllerGetEventChannels = (
      query?: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit?: number;
        /**
         * @min 0
         * @example 0
         */
        page?: number;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetEventChannelsRes, BaseErrorResponse>({
        path: '/api3/event/channel',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Event translation
     * @name EventTranslationControllerGetEventTranslationList
     * @summary Returns a list of event translations
     * @request GET:/api3/event/translation
     * @secure
     * @response `200` `GetEventTranslationListRes`
     * @response `400` `BaseErrorResponse`
     */
    eventTranslationControllerGetEventTranslationList = (
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        eventTypeId?: number;
        languageId?: string;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetEventTranslationListRes, BaseErrorResponse>({
        path: '/api3/event/translation',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Event translation
     * @name EventTranslationControllerCreateEventTranslation
     * @summary Creates a new event translation
     * @request POST:/api3/event/translation
     * @secure
     * @response `200` `GetEventTranslationRes`
     * @response `400` `BaseErrorResponse`
     */
    eventTranslationControllerCreateEventTranslation = (data: AddEventTranslationReq, params: RequestParams = {}) =>
      this.request<GetEventTranslationRes, BaseErrorResponse>({
        path: '/api3/event/translation',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Event translation
     * @name EventTranslationControllerUpdateEventTranslation
     * @summary Updates event translation
     * @request PUT:/api3/event/translation/{id}
     * @secure
     * @response `200` `GetEventTranslationRes`
     * @response `400` `BaseErrorResponse`
     */
    eventTranslationControllerUpdateEventTranslation = (
      id: number,
      data: UpdateEventTranslationReq,
      params: RequestParams = {},
    ) =>
      this.request<GetEventTranslationRes, BaseErrorResponse>({
        path: `/api3/event/translation/${id}`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Event translation
     * @name EventTranslationControllerDeleteEventTranslation
     * @summary Deletes event translation
     * @request DELETE:/api3/event/translation/{id}
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    eventTranslationControllerDeleteEventTranslation = (id: number, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/event/translation/${id}`,
        method: 'DELETE',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Event type
     * @name EventTypeControllerGetEventTypeList
     * @summary Returns a list of event types
     * @request GET:/api3/event/type
     * @secure
     * @response `200` `GetEventTypeListRes`
     * @response `400` `BaseErrorResponse`
     */
    eventTypeControllerGetEventTypeList = (
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetEventTypeListRes, BaseErrorResponse>({
        path: '/api3/event/type',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Event type
     * @name EventTypeControllerUpdateEventType
     * @summary Updates event type
     * @request PUT:/api3/event/type/{id}
     * @secure
     * @response `200` `GetEventTypeRes`
     * @response `400` `BaseErrorResponse`
     */
    eventTypeControllerUpdateEventType = (id: number, data: UpdateEventTypeReq, params: RequestParams = {}) =>
      this.request<GetEventTypeRes, BaseErrorResponse>({
        path: `/api3/event/type/${id}`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Event template
     * @name EventTemplateControllerGetEventTemplateList
     * @summary Returns a list of event templates
     * @request GET:/api3/event/template
     * @secure
     * @response `200` `GetEventTemplatesRes`
     * @response `400` `BaseErrorResponse`
     */
    eventTemplateControllerGetEventTemplateList = (
      query?: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit?: number;
        /**
         * @min 0
         * @example 0
         */
        page?: number;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetEventTemplatesRes, BaseErrorResponse>({
        path: '/api3/event/template',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Event template
     * @name EventTemplateControllerCreateEventTemplate
     * @summary Creates a new event template
     * @request POST:/api3/event/template
     * @secure
     * @response `200` `GetEventTemplateRes`
     * @response `400` `BaseErrorResponse`
     */
    eventTemplateControllerCreateEventTemplate = (data: AddEventTemplateReq, params: RequestParams = {}) =>
      this.request<GetEventTemplateRes, BaseErrorResponse>({
        path: '/api3/event/template',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Event template
     * @name EventTemplateControllerUpdateEventTemplate
     * @summary Updates event template
     * @request PUT:/api3/event/template/{id}
     * @secure
     * @response `200` `GetEventTemplateRes`
     * @response `400` `BaseErrorResponse`
     */
    eventTemplateControllerUpdateEventTemplate = (
      id: number,
      data: UpdateEventTemplateReq,
      params: RequestParams = {},
    ) =>
      this.request<GetEventTemplateRes, BaseErrorResponse>({
        path: `/api3/event/template/${id}`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Event template
     * @name EventTemplateControllerDeleteEventTemplate
     * @summary Deletes event template
     * @request DELETE:/api3/event/template/{id}
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    eventTemplateControllerDeleteEventTemplate = (id: number, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/event/template/${id}`,
        method: 'DELETE',
        secure: true,
        format: 'json',
        ...params,
      });
  }

  return new Event();
}
